import { first, last } from 'lodash';

import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatDateToMonDDYYYY } from '@/utils/formatting/dates';

import { PresentationString_HouseholdFragment } from './graphql/PresentationString.generated';

export interface PresentationStringQueryData {
  household: PresentationString_HouseholdFragment | null;
  presentationCreatedAt: Date | null;
}

export interface PresentationStringVariable {
  text: PresentationStringVariableText;
  formatter: (queryData: PresentationStringQueryData | null) => string | null;
}

// https://www.notion.so/withluminary/Client-presentation-variables-2cd65ff1a4e280f181b7c08429ad6a93?source=copy_link
// NOTE NOTE NOTE: YOU CANNOT CHANGE THESE DIISPLAY VALUES BECAUSE IT WILL BREAK BACKWARDS COMPATIBILITY FOR PRESENTATIONS
// THAT ALREADY INCLUDE THESE.
enum PresentationStringVariableText {
  HOUSEHOLD_NAME = 'Household name',
  CLIENT_ONE_FIRST_NAME = 'Client one first name',
  CLIENT_ONE_LAST_NAME = 'Client one last name',
  CLIENT_TWO_FIRST_NAME = 'Client two first name',
  CLIENT_TWO_LAST_NAME = 'Client two last name',
  CURRENT_NET_WORTH = 'Current net worth',
  CURRENT_IN_ESTATE_ASSETS = 'Current in-estate assets',
  CURRENT_OUT_OF_ESTATE_ASSETS = 'Current out-of-estate assets',
  CURRENT_DATE = 'Current date',
  CLIENT_ONE_REMAINING_LIFETIME_EXEMPTION = 'Client one remaining lifetime exemption',
  CLIENT_TWO_REMAINING_LIFETIME_EXEMPTION = 'Client two remaining lifetime exemption',
  CLIENT_ONE_REMAINING_GST_EXEMPTION = 'Client one remaining GST exemption',
  CLIENT_TWO_REMAINING_GST_EXEMPTION = 'Client two remaining GST exemption',
  PRESENTATION_CREATED = 'Presentation created',
}

const HouseholdNameVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.HOUSEHOLD_NAME,
  formatter: (queryData) => queryData?.household?.displayName ?? null,
};

const ClientOneFirstNameVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CLIENT_ONE_FIRST_NAME,
  formatter: (queryData) => {
    const client = first(queryData?.household?.possiblePrimaryClients);
    return client ? client.preferredName?.trim() || client.firstName : null;
  },
};

const ClientOneLastNameVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CLIENT_ONE_LAST_NAME,
  formatter: (queryData) =>
    first(queryData?.household?.possiblePrimaryClients)?.lastName ?? null,
};

const ClientTwoFirstNameVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CLIENT_TWO_FIRST_NAME,
  formatter: (queryData) => {
    const clients = queryData?.household?.possiblePrimaryClients;
    if (!clients || clients.length < 2) return null;
    const client = last(clients);
    return client ? client.preferredName?.trim() || client.firstName : null;
  },
};

const ClientTwoLastNameVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CLIENT_TWO_LAST_NAME,
  formatter: (queryData) => {
    const clients = queryData?.household?.possiblePrimaryClients;
    if (!clients || clients.length < 2) return null;
    return last(clients)?.lastName ?? null;
  },
};

const CurrentNetWorthVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CURRENT_NET_WORTH,
  formatter: (queryData) => {
    const totalEstateValue =
      queryData?.household?.estateValueMetrics?.totalEstateValue ?? null;

    if (!totalEstateValue) return null;

    return formatCurrencyNoDecimals(totalEstateValue);
  },
};

const CurrentInEstateAssetsVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CURRENT_IN_ESTATE_ASSETS,
  formatter: (queryData) => {
    const inEstateValue =
      queryData?.household?.estateValueMetrics?.inEstateValue ?? null;

    if (!inEstateValue) return null;

    return formatCurrencyNoDecimals(inEstateValue);
  },
};

const CurrentOutOfEstateAssetsVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CURRENT_OUT_OF_ESTATE_ASSETS,
  formatter: (queryData) => {
    const outOfEstateValue =
      queryData?.household?.estateValueMetrics?.outOfEstateValue ?? null;

    if (!outOfEstateValue) return null;

    return formatCurrencyNoDecimals(outOfEstateValue);
  },
};

const CurrentDateVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CURRENT_DATE,
  // Return null when there's no household context (e.g. editing a tenant-level
  // template) so the variable token is left unreplaced, consistent with all
  // other variables that depend on household data.
  formatter: (queryData) => {
    if (!queryData?.household) return null;
    return formatDateToMonDDYYYY(new Date());
  },
};

const ClientOneRemainingLifetimeExemptionVariable: PresentationStringVariable =
  {
    text: PresentationStringVariableText.CLIENT_ONE_REMAINING_LIFETIME_EXEMPTION,
    formatter: (queryData) => {
      const exemption =
        first(queryData?.household?.possiblePrimaryClients)
          ?.remainingLifetimeExclusion ?? null;
      if (!exemption) return null;
      return formatCurrencyNoDecimals(exemption);
    },
  };

const ClientTwoRemainingLifetimeExemptionVariable: PresentationStringVariable =
  {
    text: PresentationStringVariableText.CLIENT_TWO_REMAINING_LIFETIME_EXEMPTION,
    formatter: (queryData) => {
      const clients = queryData?.household?.possiblePrimaryClients;
      if (!clients || clients.length < 2) return null;
      const exemption = last(clients)?.remainingLifetimeExclusion ?? null;
      if (!exemption) return null;
      return formatCurrencyNoDecimals(exemption);
    },
  };

const ClientOneRemainingGstExemptionVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CLIENT_ONE_REMAINING_GST_EXEMPTION,
  formatter: (queryData) => {
    const exemption =
      first(queryData?.household?.possiblePrimaryClients)
        ?.remainingGSTExclusion ?? null;
    if (!exemption) return null;
    return formatCurrencyNoDecimals(exemption);
  },
};

const ClientTwoRemainingGstExemptionVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.CLIENT_TWO_REMAINING_GST_EXEMPTION,
  formatter: (queryData) => {
    const clients = queryData?.household?.possiblePrimaryClients;
    if (!clients || clients.length < 2) return null;
    const exemption = last(clients)?.remainingGSTExclusion ?? null;
    if (!exemption) return null;
    return formatCurrencyNoDecimals(exemption);
  },
};

const PresentationCreatedVariable: PresentationStringVariable = {
  text: PresentationStringVariableText.PRESENTATION_CREATED,
  formatter: (queryData) => {
    const createdAt = queryData?.presentationCreatedAt;
    if (!createdAt) return null;
    return formatDateToMonDDYYYY(new Date(createdAt));
  },
};

/** Check whether a variable name (the text inside `{…}` braces) corresponds
 *  to one of the known presentation string variables. This is intentionally
 *  separate from `resolveVariable` because `resolveVariable` returns `null`
 *  for both unknown variables AND known variables with missing data — we want
 *  to highlight known variables even when data isn't loaded yet. */
export function isKnownVariable(name: string): boolean {
  return Object.hasOwn(PRESENTATION_STRINGS, name);
}

export const CLIENT_TWO_VARIABLE_NAMES: ReadonlySet<string> = new Set([
  PresentationStringVariableText.CLIENT_TWO_FIRST_NAME,
  PresentationStringVariableText.CLIENT_TWO_LAST_NAME,
  PresentationStringVariableText.CLIENT_TWO_REMAINING_LIFETIME_EXEMPTION,
  PresentationStringVariableText.CLIENT_TWO_REMAINING_GST_EXEMPTION,
]);

export const PRESENTATION_STRINGS: Record<
  PresentationStringVariableText,
  PresentationStringVariable
> = {
  [PresentationStringVariableText.HOUSEHOLD_NAME]: HouseholdNameVariable,
  [PresentationStringVariableText.CLIENT_ONE_FIRST_NAME]:
    ClientOneFirstNameVariable,
  [PresentationStringVariableText.CLIENT_ONE_LAST_NAME]:
    ClientOneLastNameVariable,
  [PresentationStringVariableText.CLIENT_TWO_FIRST_NAME]:
    ClientTwoFirstNameVariable,
  [PresentationStringVariableText.CLIENT_TWO_LAST_NAME]:
    ClientTwoLastNameVariable,
  [PresentationStringVariableText.CURRENT_NET_WORTH]: CurrentNetWorthVariable,
  [PresentationStringVariableText.CURRENT_IN_ESTATE_ASSETS]:
    CurrentInEstateAssetsVariable,
  [PresentationStringVariableText.CURRENT_OUT_OF_ESTATE_ASSETS]:
    CurrentOutOfEstateAssetsVariable,
  [PresentationStringVariableText.CURRENT_DATE]: CurrentDateVariable,
  [PresentationStringVariableText.CLIENT_ONE_REMAINING_LIFETIME_EXEMPTION]:
    ClientOneRemainingLifetimeExemptionVariable,
  [PresentationStringVariableText.CLIENT_TWO_REMAINING_LIFETIME_EXEMPTION]:
    ClientTwoRemainingLifetimeExemptionVariable,
  [PresentationStringVariableText.CLIENT_ONE_REMAINING_GST_EXEMPTION]:
    ClientOneRemainingGstExemptionVariable,
  [PresentationStringVariableText.CLIENT_TWO_REMAINING_GST_EXEMPTION]:
    ClientTwoRemainingGstExemptionVariable,
  [PresentationStringVariableText.PRESENTATION_CREATED]:
    PresentationCreatedVariable,
};
